import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../../utils/api";
import "./Login.css";

const ForgotPassword = () => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [cooldownTime, setCooldownTime] = useState(0);
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const navigate = useNavigate();

    // For countdown timer if rate limited
    React.useEffect(() => {
        let timer: NodeJS.Timeout | null = null;
        if (cooldownTime > 0) {
            timer = setInterval(() => {
                setCooldownTime(prev => {
                    if (prev <= 1) {
                        if (timer) clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [cooldownTime]);

    // Validate email to enable/disable the button
    React.useEffect(() => {
        // Basic email validation regex
        const isEmailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.toLowerCase());
        setIsButtonDisabled(!isEmailValid);
    }, [email]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Prevent multiple submissions
        if (isLoading || cooldownTime > 0) return;

        // Prevent submission if button is disabled due to validation
        if (isButtonDisabled) return;

        setIsLoading(true);
        setError("");
        setSuccess("");

        try {
            console.log("Requesting password reset for:", email);

            // Call the password reset service
            await authService.requestPasswordReset(email);

            console.log("Password reset request successful");
            setSuccess("Email sent to reset password, may take a few moments, please check spam folder.");

        } catch (err: any) {
            console.error("Password reset error:", err);
            let errorMessage = "An error occurred while processing your request.";

            // Handle rate limiting (429 Too Many Requests)
            if (err.response?.status === 429) {
                // Set a cooldown timer (for development, 10 seconds; for production would be longer)
                setCooldownTime(30);
                errorMessage = "Please wait before requesting another password reset. You can try again in a few moments.";
            }
            // Handle other error responses
            else if (err.response?.data) {
                // Handle structured error response
                if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data;
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                } else if (err.response.data.error) {
                    errorMessage = err.response.data.error;
                }
            } else if (err.response?.status === 404) {
                // Don't expose that email wasn't found (security best practice)
                errorMessage = "If your email is registered, you will receive reset instructions shortly.";
            } else if (err.request) {
                errorMessage = "Server not responding. Please try again later.";
            } else if (err.message) {
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-header">
                <img src="/assets/logos/Logo_Parallel.svg" alt="Parallel Logo" className="parallel-logo" />
            </div>
            <div className="login-box">
                <img src="/assets/logos/Logo_Operator_Inline.svg" alt="Parallel Operator" className="logo-operator-inline" />

                <form onSubmit={handleSubmit}>
                    <p className="reset-instructions">Please verify your email address, and we'll send you a link to reset your password.</p>

                    <div className="input-group">
                        <input
                            type="email"
                            placeholder=" "
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                            name="email"
                            disabled={isLoading || cooldownTime > 0}
                            required
                        />
                        <label htmlFor="email">Email Address</label>
                    </div>

                    <button
                        type="submit"
                        className={`login-button ${isLoading ? 'loading' : ''}${isButtonDisabled ? ' login-button-disabled' : ''}`}
                        disabled={isLoading || cooldownTime > 0 || isButtonDisabled}
                    >
                        {isLoading ? (
                            'Sending...'
                        ) : (
                            cooldownTime > 0 ? `Retry in ${cooldownTime}s` : (
                                <>
                                    <img src="/assets/send.svg" alt="Send Email" className="login-button-icon" width="18" height="18" />
                                    Send Email
                                </>
                            )
                        )}
                    </button>

                    {error && <p className="error">{error}</p>}
                    {success && <p className="success">{success}</p>}
                </form>

                <button
                    className="back-to-login"
                    onClick={() => navigate('/login')}
                    disabled={isLoading}
                >
                    Back to Login
                </button>
            </div>
        </div>
    );
};

export default ForgotPassword; 